/**
 * Business Profile API Client
 *
 * Manages the user's business profile (1:1 relationship).
 */

import axios from 'axios';
import {
  BusinessProfile,
  BusinessProfileSetupRequest,
  BusinessProfileUpdateRequest,
  BusinessProfileResponse,
  BusinessStatusResponse,
} from '@/types/business';
import { ApiResponse } from '@/types';
import apiClient, { handleApiResponse } from './client';

class BusinessApiClient {
  private basePath = '/v1/business';

  /**
   * Get current user's business profile.
   * Returns null if not setup (user hasn't completed onboarding).
   */
  async getProfile(): Promise<BusinessProfile | null> {
    try {
      const response = await apiClient.get<BusinessProfileResponse>(
        `${this.basePath}/profile`
      );
      return response.data;
    } catch (error) {
      // 404 means profile doesn't exist yet - this is expected for new users
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Check if user has completed business setup.
   */
  async isSetupComplete(): Promise<boolean> {
    try {
      const response = await apiClient.get<BusinessStatusResponse>(
        `${this.basePath}/profile/status`
      );
      return response.is_complete;
    } catch (error) {
      // 404 means no profile = not complete
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return false;
      }
      throw error;
    }
  }

  /**
   * Initial business profile setup (onboarding).
   */
  async setupProfile(
    data: BusinessProfileSetupRequest
  ): Promise<BusinessProfile> {
    const response = await apiClient.post<ApiResponse<BusinessProfile>>(
      `${this.basePath}/profile/setup`,
      data
    );
    return handleApiResponse(response);
  }

  /**
   * Update business profile.
   */
  async updateProfile(
    data: BusinessProfileUpdateRequest
  ): Promise<BusinessProfile> {
    const response = await apiClient.put<ApiResponse<BusinessProfile>>(
      `${this.basePath}/profile`,
      data
    );
    return handleApiResponse(response);
  }
}

export const businessApi = new BusinessApiClient();
